import { useGlobal } from 'hooks/useGlobal';
import { useRecoilValue } from 'recoil';

import * as K from 'common/constants';
import ELEMENT from 'common/element';
import { getDurationText } from 'common/util/time';
import { isComplexExpression, tokenizeFilter } from 'common/util/filter/parse';
import { TOKEN_TYPE } from 'common/util/filter/types';
import { extractFacetExpression, hasFunction } from 'common/util/graph';
import { queryTimeRangeState } from 'components/common/Managers/RouteStateManager.state';

import TimelineLegend from 'components/common/Legends/TimelineLegend';


type GraphHeaderProps = {
  facet:               string;
  breakdownFacet?:     string;
  compareTimeOffset?:  string;
  logsPageLink?:       string;
  isBarChart?:         boolean;
  barWidth?:           number;
  filter?:              string;
};

const DEFAULT_GRAPH_TITLE = 'Graph of matching events';

/**
 * This component is the header for a graph on the Search Graphs page.
 *
 * @prop facet                 facet being graphed
 * @prop breakdownFacet        breakdown facet
 * @prop compareTimeOffset     the compare time offset
 * @prop logsPageLink          link to events page for this search
 * @prop barWidth              bucket size for bar graph
 * @prop filter                 the filter
 */
function GraphHeader({
  facet,
  breakdownFacet,
  compareTimeOffset,
  logsPageLink,
  isBarChart,
  barWidth,
  filter,
}: GraphHeaderProps): JSX.Element {

  const queryTimeRange = useRecoilValue(queryTimeRangeState);
  const { startTime, endTime } = queryTimeRange;
  const [ selectionRange ] = useGlobal(K.GLOBAL_KEY.CHART_SELECTION_RANGE);
  const { start: rangeStart, end: rangeEnd } = selectionRange;

  let timeRange;
  if (rangeStart && rangeEnd) {
    timeRange = { startTime: rangeStart, endTime: rangeEnd };
  } else if (startTime && endTime) {
    timeRange = { startTime: parseFloat(startTime), endTime: parseFloat(endTime) };
  }

  let title = isBarChart ? DEFAULT_GRAPH_TITLE : DEFAULT_GRAPH_TITLE + ' / second';
  let subtitle = breakdownFacet ? `broken down by ${breakdownFacet}` : '';
  if ((facet && facet !== K.RATE.op) || (filter && isComplexExpression(filter))) {
    title = 'Graph of'; // Special highlighted box for facet term to complete title.
  }

  let facetText = facet;
  // facet compare graphs and facet breakdown graphs include the facet function
  if (facet && (breakdownFacet || compareTimeOffset)) {
    facetText = !hasFunction(facet) ? `mean(${facet})` : facet;

    // standard facet graphs do no include facet function and are show via the search graph legend
  } else if (facet) {
    const parsedFacet = facet ? extractFacetExpression(facet) : null;
    facetText = parsedFacet?.facetVariable || facet;

    // complex expressions in standard graph page show the facet function
  } else if (filter && isComplexExpression(filter)) {
    const tokens = tokenizeFilter(filter);
    const functionTokens = tokens.filter((token) => token.type === TOKEN_TYPE.FUNCTION);

    // If the filter has a complex expression, highlight the first field in the filter
    if (functionTokens.length === 1) {
      const firstField = tokens.find((token) => token.type === TOKEN_TYPE.FIELD);
      facetText = firstField?.text || facetText;

      // if the complex expression has multiple functions, then we just have the title as 'Graph'
    } else if (functionTokens.length > 1) {
      title = 'Graph';
    }
  }

  if (barWidth && isBarChart) {
    subtitle += ` (${getDurationText(barWidth)} / bar)`;
  }

  return (
    <>
      <TimelineLegend
        title={title}
        subtitle={subtitle}
        facet={facetText}
        timeRange={timeRange}
        prependDate
        buttonText="Show Logs"
        buttonLink={logsPageLink}
        buttonTestId={ELEMENT.timelineLegendShowLogs}
      />
    </>
  );
}

export default GraphHeader;
